/**
 * Canvas avatar hook for Picture-in-Picture.
 *
 * 故意不命名为 hooks.ts：web 构建会优先解析 hooks.web.ts，
 * 若同目录再放 hooks.ts，`import … from '../hooks'` 会拿不到本文件的导出，
 * 运行时出现 useCanvasAvatar is not a function。
 */
import { useEffect, useRef, useState } from 'react';
import IconUserSVG from '../base/icons/svg/user.svg?raw';
import { TILE_ASPECT_RATIO } from '../filmstrip/constants';
import { renderAvatarOnCanvas } from './functions';
import logger from './logger';
/**
 * Canvas dimensions for PiP avatar rendering.
 */
const CANVAS_WIDTH = 640;
const CANVAS_HEIGHT = Math.floor(CANVAS_WIDTH / TILE_ASPECT_RATIO);
/**
 * 画布采集帧率。勿用 0：部分浏览器在 PiP 中不会持续展示 requestFrame 的单帧，导致小窗黑屏。
 */
const CANVAS_CAPTURE_FPS = 5;
/**
 * Loads and prepares the default user icon SVG as an Image element.
 *
 * @returns {HTMLImageElement} The prepared image element.
 */
function createDefaultIconImage() {
    let svgText = IconUserSVG;
    if (!svgText.includes('fill=')) {
        svgText = svgText.replace('<svg', '<svg fill="#FFFFFF"');
    }
    const dataUrl = `data:image/svg+xml,${encodeURIComponent(svgText)
        .replace(/'/g, '%27')
        .replace(/"/g, '%22')}`;
    const img = new Image();
    img.src = dataUrl;
    return img;
}
/**
 * Custom hook that manages canvas-based avatar rendering for Picture-in-Picture.
 * Creates and maintains a canvas element with a MediaStream that can be used
 * as a video source when the participant's video is unavailable.
 *
 * @param {IUseCanvasAvatarOptions} options - The hook options.
 * @returns {IUseCanvasAvatarResult} The canvas stream for use as video source.
 */
export function useCanvasAvatar(options) {
    const { participant, displayName, customAvatarBackgrounds, backgroundColor, fontFamily, initialsColor, displayNameColor } = options;
    const refs = useRef({
        canvas: null,
        defaultIcon: null
    });
    const streamRef = useRef(null);
    const [canvasStreamReady, setCanvasStreamReady] = useState(false);
    /**
     * Initialize canvas, stream, and default icon on mount.
     */
    useEffect(() => {
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_WIDTH;
        canvas.height = CANVAS_HEIGHT;
        refs.current.canvas = canvas;
        streamRef.current = canvas.captureStream(CANVAS_CAPTURE_FPS);
        refs.current.defaultIcon = createDefaultIconImage();
        logger.info(`[画中画] ${new Date().toISOString()} 头像画布流已初始化 fps=${CANVAS_CAPTURE_FPS}`);
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            refs.current.canvas = null;
            refs.current.defaultIcon = null;
            setCanvasStreamReady(false);
            logger.info(`[画中画] ${new Date().toISOString()} 头像画布流已清理`);
        };
    }, []);
    /**
     * Re-render avatar when participant or display name changes.
     */
    useEffect(() => {
        const { canvas, defaultIcon } = refs.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            logger.error('[画中画] 无法获取 canvas 2D 上下文');
            return;
        }
        renderAvatarOnCanvas(canvas, ctx, participant, displayName, customAvatarBackgrounds, defaultIcon, backgroundColor, fontFamily, initialsColor, displayNameColor).then(() => {
            const track = streamRef.current?.getVideoTracks()[0];
            if (track?.requestFrame) {
                track.requestFrame();
            }
            setCanvasStreamReady(true);
            logger.info(`[画中画] ${new Date().toISOString()} 头像帧已绘制 name=${displayName || '-'}`);
        }).catch((error) => {
            logger.error(`[画中画] 绘制头像失败: ${error?.message || error}`, error);
        });
    }, [participant?.loadableAvatarUrl, participant?.name, displayName, customAvatarBackgrounds, backgroundColor, fontFamily, initialsColor, displayNameColor]);
    return {
        canvasStreamRef: streamRef,
        canvasStreamReady
    };
}
